import { AppStore } from "../store/store.ts";
import {
  selectCurrentState,
  selectSession,
} from "../store/selectors/select.slice.ts";
import { webSocketSetter } from "./websocket-store.ts";
import { produceWebsocket } from "./websocket-utils.ts";
import {
  SessionStateEnum,
  SocketMessageDtoV2EventTypeEnum,
} from "../orchestrator-api-client";
import { debug } from "../utilities/logger.ts";
import { CoralogixLogSeverity, CoralogixRum } from "@coralogix/browser";

const TIMEOUT_KEEP_ALIVE = 10000;
const TIMEOUT_PONG = 250;
const keepAliveStore: {
  keepAliveInterval: number | undefined;
  pongTimeout: number | undefined;
} = {
  keepAliveInterval: undefined,
  pongTimeout: undefined,
};

export const clearPongTimeout = () => {
  if (typeof keepAliveStore.pongTimeout !== "undefined") {
    window.clearTimeout(keepAliveStore.pongTimeout);
    keepAliveStore.pongTimeout = undefined;
  }
};

export const startKeepAlive = (
  store: AppStore,
  wsGetter: () => WebSocket | undefined,
  ws_endpoint: string,
) => {
  const state = store.getState();
  const currentState = selectCurrentState(state);
  const { keepAlive } = selectSession(state);

  if (typeof keepAliveStore.keepAliveInterval !== "undefined") {
    return;
  }

  const onVisible = () => {
    if (!keepAlive) return;
    if (document.visibilityState === "visible") {
      if (
        currentState !== SessionStateEnum.DeclinedByRules &&
        currentState !== SessionStateEnum.BureauKo &&
        currentState !== SessionStateEnum.Privacy &&
        currentState !== SessionStateEnum.Init
      ) {
        wsGetter()?.send(
          JSON.stringify({
            eventType: SocketMessageDtoV2EventTypeEnum.GetCurrentStatus,
          }),
        );
      }
      webSocketKeepAlive(store, wsGetter, ws_endpoint);
    }
  };

  const onReconnect = () => {
    if (!keepAlive) return;
    if (
      currentState !== SessionStateEnum.DeclinedByRules &&
      currentState !== SessionStateEnum.BureauKo &&
      currentState !== SessionStateEnum.Privacy &&
      currentState !== SessionStateEnum.Init
    ) {
      wsGetter()?.send(
        JSON.stringify({
          eventType: SocketMessageDtoV2EventTypeEnum.GetCurrentStatus,
        }),
      );
    }
    webSocketKeepAlive(store, wsGetter, ws_endpoint);
  };

  document.addEventListener("visibilitychange", onVisible);

  window.addEventListener("online", onReconnect);

  keepAliveStore.keepAliveInterval = window.setInterval(
    webSocketKeepAlive.bind(undefined, store, wsGetter, ws_endpoint),
    TIMEOUT_KEEP_ALIVE,
  );
};

export const stopKeepAlive = () => {
  if (typeof keepAliveStore.keepAliveInterval === "undefined") {
    return;
  }

  window.clearInterval(keepAliveStore.keepAliveInterval);
  keepAliveStore.keepAliveInterval = undefined;
  clearPongTimeout();
};

const webSocketKeepAlive = (
  store: AppStore,
  wsGetter: () => WebSocket | undefined,
  ws_endpoint: string,
) => {
  const ws: WebSocket | undefined = wsGetter();
  const state = store.getState();
  const { keepAlive, token } = selectSession(state);
  const currentState = selectCurrentState(state);

  if (typeof token === "undefined") {
    return;
  }

  if (typeof ws === "undefined") {
    return;
  }

  if (ws.readyState === WebSocket.OPEN) {
    //to prevent infine loader after Namirial signature, we request a current status to the websocket
    if (currentState === SessionStateEnum.KycInProgress) {
      ws.send(
        JSON.stringify({
          eventType: SocketMessageDtoV2EventTypeEnum.GetCurrentStatus,
        }),
      );
      return;
    }

    clearPongTimeout();
    keepAliveStore.pongTimeout = window.setTimeout(() => {
      debug("[WS] Pong timeout reached, reconnecting...");
      CoralogixRum.log(
        CoralogixLogSeverity.Info,
        `[WS] Pong timeout reached, reconnecting...`,
      );
      ws.close();
      const newWs = produceWebsocket(store, ws_endpoint);
      if (typeof newWs !== "undefined") {
        webSocketSetter(newWs);
      }
    }, TIMEOUT_PONG);

    ws.send(
      JSON.stringify({
        eventType: SocketMessageDtoV2EventTypeEnum.Ping,
      }),
    );
    return;
  }

  if (ws.readyState === WebSocket.CONNECTING) {
    return;
  }

  if (ws.readyState === WebSocket.CLOSING) {
    return;
  }

  if (!keepAlive) {
    ws.close();
    return;
  }

  const newWs = produceWebsocket(store, ws_endpoint);

  if (typeof newWs === "undefined") {
    return;
  }

  webSocketSetter(newWs);
};
